/**
 * The reward flight's maths (owner 2026-10-24: "the icon of it should fly into the area that it lives"): how many icons
 * a gain bursts into, where each one pops to, the curve it flies along and when it lands. Pure numbers, no DOM, so the
 * timing and the shape are pinned by tests (test/rewardFlyMath.test.ts) and the layer (flyLayer.ts) only plays them.
 *
 * A volley: every icon pops out of the spot the reward was earned onto a ring around it (BURST_MS), hangs there a
 * beat (HOLD_MS), then the icons leave one after another (FLY_STAGGER_MS) and each flies a quadratic Bézier that bows
 * to one side (sides alternate, so the stream fans out like Kingshot's) and accelerates into its home (FLIGHT_MS).
 */

export interface Point { readonly x: number; readonly y: number }

/** One frame of a sprite's animation: `offset` 0…1 of its own duration, its centre in CSS px, scale and opacity. */
export interface FlyFrame { readonly offset: number; readonly x: number; readonly y: number; readonly scale: number; readonly opacity: number }

/** When one icon of a volley pops, leaves and lands (ms from the volley's start). */
export interface IconTiming { readonly start: number; readonly leave: number; readonly land: number }

export const FLY_TIMING = {
  /** The pop out to the ring. */
  BURST_MS: 240,
  /** Each icon pops this much after the one before (a ripple, not one blob). */
  BURST_STAGGER_MS: 18,
  /** The beat on the ring before the first icon leaves. */
  HOLD_MS: 140,
  /** The gap between two icons leaving. */
  FLY_STAGGER_MS: 70,
  /** One icon's flight from the ring to its home. */
  FLIGHT_MS: 560,
  /** A second kind in the same grant (gold, then gear) starts this much after the first. */
  KIND_GAP_MS: 180,
} as const;

/** The burst's size: how many icons a gain shows (log-scaled for counted gains, one per item for unique ones). */
export const BURST = {
  /** A counted gain of 1 shows this many. */
  BASE: 3,
  /** One more icon per this much × 10 (1 → 3, 10 → 4, 100 → 6, 1 000 → 7, 10 000 → 8). */
  PER_DECADE: 1.25,
  MAX: 8,
  /** Unique items (gear, a pet, a skin) show one icon each, up to this many. */
  MAX_ITEMS: 6,
} as const;

/** The ring the icons pop onto. */
export const RING = {
  MIN_PX: 26,
  MAX_PX: 58,
  /** The golden angle (radians): consecutive icons land far apart on the ring, never in a line. */
  STEP_RAD: Math.PI * (3 - Math.sqrt(5)),
  /** The ring is squashed vertically a little (a burst reads as thrown, not drawn with a compass). */
  Y_SQUASH: 0.8,
} as const;

/** The flight's shape. */
export const CURVE = {
  /** How far the curve bows out, as a share of the straight distance. */
  BOW: 0.28,
  /** Samples along the curve (the layer interpolates linearly between them). */
  SAMPLES: 14,
  /** Scale on the ring, at the peak of the pop, and as the icon enters its home. */
  RING_SCALE: 1,
  POP_SCALE: 1.18,
  LAND_SCALE: 0.55,
  /** The last share of the flight fades the icon out (it melts into the counter rather than sitting on it). */
  FADE_FROM: 0.92,
  /** Where the pop peaks, as a share of BURST_MS. */
  POP_PEAK: 0.6,
  /** The icon is born this small at the reward's spot. */
  BIRTH_SCALE: 0.3,
} as const;

/** How many icons a gain bursts into: `unique` gains (gear, pets, skins) one per item, counted ones log-scaled. */
export function burstCount(amount: number, unique: boolean): number {
  if (!(amount > 0)) return 0;
  if (unique) return Math.min(BURST.MAX_ITEMS, Math.max(1, Math.floor(amount)));
  return Math.min(BURST.MAX, BURST.BASE + Math.round(BURST.PER_DECADE * Math.log10(Math.max(1, amount))));
}

/** Where icon `i` of `n` pops to, relative to the burst's centre: a squashed ring, golden-angle spaced, radius growing. */
export function burstOffset(i: number, n: number): Point {
  if (n <= 1) return { x: 0, y: 0 };          // a single icon pops in place
  const r = RING.MIN_PX + (RING.MAX_PX - RING.MIN_PX) * (i / (n - 1));
  const a = i * RING.STEP_RAD - Math.PI / 2;
  return { x: Math.cos(a) * r, y: Math.sin(a) * r * RING.Y_SQUASH };
}

/** The quadratic Bézier's control point: the midpoint pushed out sideways by BOW × distance (`side` ±1). */
export function bowControl(from: Point, to: Point, side: 1 | -1): Point {
  const dx = to.x - from.x, dy = to.y - from.y, len = Math.hypot(dx, dy);
  if (len === 0) return from;
  const nx = -dy / len, ny = dx / len;
  return { x: (from.x + to.x) / 2 + nx * len * CURVE.BOW * side, y: (from.y + to.y) / 2 + ny * len * CURVE.BOW * side };
}

/** A point on the quadratic Bézier p0 → p2 bent by p1, at t ∈ [0, 1]. */
export function quadBezier(p0: Point, p1: Point, p2: Point, t: number): Point {
  const u = 1 - t;
  return { x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x, y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y };
}

/** The flight's progress along the curve at time share u: eases in (slow off the ring, fast into the home). */
export function flightEase(u: number): number {
  const c = Math.min(1, Math.max(0, u));
  return c * c * (2 - c * 0.6) / 1.4;
}

/** When each icon of an `n`-icon volley pops, leaves the ring and lands (ms from the volley's start). */
export function volleyTimings(n: number): IconTiming[] {
  const T = FLY_TIMING, out: IconTiming[] = [];
  for (let i = 0; i < n; i++) {
    const start = i * T.BURST_STAGGER_MS;
    const leave = Math.max(start + T.BURST_MS, T.BURST_MS + T.HOLD_MS + i * T.FLY_STAGGER_MS);
    out.push({ start, leave, land: leave + T.FLIGHT_MS });
  }
  return out;
}

/** How long a volley of `n` icons takes from the first pop to the last landing (0 for none). */
export function volleyDuration(n: number): number {
  const t = volleyTimings(n);
  return t.length === 0 ? 0 : t[t.length - 1].land;
}

/**
 * One icon's frames, from its birth at `origin` to its home at `target`: the pop onto `ring` (origin + its burst
 * offset), the hold, then the bowed flight. `timing` places the phases inside the sprite's own animation (which starts
 * at `timing.start` and lasts `timing.land − timing.start`).
 */
export function iconFrames(origin: Point, ring: Point, target: Point, side: 1 | -1, timing: IconTiming): FlyFrame[] {
  const total = timing.land - timing.start;
  const at = (ms: number) => (total <= 0 ? 1 : Math.min(1, Math.max(0, (ms - timing.start) / total)));
  const frames: FlyFrame[] = [
    { offset: 0, x: origin.x, y: origin.y, scale: CURVE.BIRTH_SCALE, opacity: 0 },
    { offset: at(timing.start + FLY_TIMING.BURST_MS * CURVE.POP_PEAK), x: ring.x, y: ring.y, scale: CURVE.POP_SCALE, opacity: 1 },
    { offset: at(timing.start + FLY_TIMING.BURST_MS), x: ring.x, y: ring.y, scale: CURVE.RING_SCALE, opacity: 1 },
  ];
  const leave = at(timing.leave), ctrl = bowControl(ring, target, side);
  for (let s = 0; s <= CURVE.SAMPLES; s++) {
    const u = s / CURVE.SAMPLES, p = quadBezier(ring, ctrl, target, flightEase(u));
    const scale = CURVE.RING_SCALE + (CURVE.LAND_SCALE - CURVE.RING_SCALE) * u;
    const opacity = u <= CURVE.FADE_FROM ? 1 : 1 - (u - CURVE.FADE_FROM) / (1 - CURVE.FADE_FROM);
    frames.push({ offset: leave + (1 - leave) * u, x: p.x, y: p.y, scale, opacity });
  }
  // keyframe offsets must never go backwards (a 0-length hold would tie two of them)
  for (let k = 1; k < frames.length; k++) if (frames[k].offset < frames[k - 1].offset) frames[k] = { ...frames[k], offset: frames[k - 1].offset };
  return frames;
}
